import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  FaUsers,
  FaWhatsapp,
  FaSms,
  FaPhoneAlt,
  FaRobot,
  FaCheckCircle,
  FaUserFriends,
  FaCommentDots,
  FaEnvelope,
  FaBell,
  FaPhoneVolume,
  FaReply,
  FaChartBar,
  FaCalendarPlus,
  FaArrowRight,
  FaFilter,
} from "react-icons/fa";
import { MdCampaign } from "react-icons/md";
import "../../Styles/AdminCommunicationDashboard.css";

function AdminCommunicationDashboard() {
  // ---------- TOP STAT CARDS ----------
  const statCards = [
    { icon: <FaUsers />, label: "Total Voters", value: "2,26,765", change: "+5.6%", note: "vs last 30 days", color: "admincommu123-ic-blue" },
    { icon: <FaWhatsapp />, label: "WhatsApp Sent", value: "1,25,800", change: "+12.4%", color: "admincommu123-ic-green" },
    { icon: <FaSms />, label: "SMS Sent", value: "2,45,600", change: "+8.7%", color: "admincommu123-ic-blue" },
    { icon: <FaPhoneAlt />, label: "Calls Made", value: "48,250", change: "+10.1%", color: "admincommu123-ic-purple" },
    { icon: <FaRobot />, label: "AI Calls", value: "18,400", change: "+15.3%", color: "admincommu123-ic-orange" },
    { icon: <FaCheckCircle />, label: "Successful Calls", value: "16,920", change: "+14.2%", color: "admincommu123-ic-teal" },
    { icon: <FaUserFriends />, label: "Team Meetings", value: "126", change: "+6.8%", color: "admincommu123-ic-indigo" },
    { icon: <FaCommentDots />, label: "Voter Responses", value: "32,500", change: "+13.9%", color: "admincommu123-ic-pink" },
  ];

  // ---------- CHANNEL PERFORMANCE DONUT ----------
  const channelData = [
    { name: "WhatsApp", value: 125800, percent: "19.9%", color: "#22c55e" },
    { name: "SMS", value: 245600, percent: "38.8%", color: "#3b82f6" },
    { name: "Email", value: 28400, percent: "4.5%", color: "#f59e0b" },
    { name: "Push Notifications", value: 74200, percent: "11.7%", color: "#8b5cf6" },
    { name: "AI Calls", value: 83950, percent: "13.2%", color: "#06b6d4" },
    { name: "Manual Calls", value: 75100, percent: "11.9%", color: "#ec4899" },
  ];

  // ---------- DELIVERY SUMMARY TREND ----------
  const trendData = [
    { day: "11 Aug", rate: 60 },
    { day: "12 Aug", rate: 68 },
    { day: "13 Aug", rate: 55 },
    { day: "14 Aug", rate: 72 },
    { day: "15 Aug", rate: 78 },
    { day: "16 Aug", rate: 66 },
    { day: "17 Aug", rate: 70 },
  ];

  // ---------- ENGAGEMENT BY CHANNEL (HORIZONTAL BAR) ----------
  const engagementData = [
    { channel: "WhatsApp", rate: 72.6, color: "#22c55e" },
    { channel: "SMS", rate: 58.3, color: "#3b82f6" },
    { channel: "Email", rate: 41.8, color: "#f59e0b" },
    { channel: "Push Notifications", rate: 63.2, color: "#8b5cf6" },
    { channel: "AI Calls", rate: 68.9, color: "#06b6d4" },
    { channel: "Manual Calls", rate: 44.7, color: "#ec4899" },
  ];

  // ---------- TOP CAMPAIGNS ----------
  const topCampaigns = [
    { icon: <FaWhatsapp />, title: "Independence Day Greetings", channel: "WhatsApp", date: "15 Aug 2025", sent: "1,25,000", rate: "68.4%", color: "admincommu123-ic-green" },
    { icon: <FaSms />, title: "Public Meeting Invitation", channel: "SMS", date: "12 Aug 2025", sent: "78,450", rate: "62.1%", color: "admincommu123-ic-blue" },
    { icon: <FaEnvelope />, title: "Survey – Development Works", channel: "Email", date: "10 Aug 2025", sent: "28,400", rate: "41.2%", color: "admincommu123-ic-orange" },
    { icon: <FaBell />, title: "App Update – New Features", channel: "Push", date: "09 Aug 2025", sent: "66,200", rate: "61.7%", color: "admincommu123-ic-purple" },
    { icon: <FaRobot />, title: "AI Call Campaign – Feedback", channel: "AI Calls", date: "08 Aug 2025", sent: "18,400", rate: "69.3%", color: "admincommu123-ic-teal" },
  ];

  // ---------- RECENT CAMPAIGN PERFORMANCE (GROUPED BAR) ----------
  const campaignPerformance = [
    { channel: "WhatsApp", Sent: 125800, Delivered: 119300, Read: 85350, Replies: 12400 },
    { channel: "SMS", Sent: 245600, Delivered: 231400, Read: 135300, Replies: 8900 },
    { channel: "Email", Sent: 28400, Delivered: 26400, Read: 11870, Replies: 3120 },
    { channel: "Push", Sent: 74200, Delivered: 69850, Read: 39850, Replies: 4130 },
    { channel: "AI Calls", Sent: 18400, Delivered: 16920, Read: 16800, Replies: 6100 },
    { channel: "Manual Calls", Sent: 75100, Delivered: 44700, Read: 21500, Replies: 3120 },
  ];

  // ---------- COMMUNICATION TIMELINE ----------
  const timeline = [
    { title: "WhatsApp Campaign – Independence Day Greetings", time: "15 Aug 2025, 08:15 AM", status: "Read", color: "admincommu123-status-green" },
    { title: "SMS – Public Meeting Invitation", time: "12 Aug 2025, 07:20 AM", status: "Delivered", color: "admincommu123-status-blue" },
    { title: "AI Call – Public Meeting Reminder", time: "11 Aug 2025, 10:20 AM", status: "Completed", color: "admincommu123-status-teal" },
    { title: "Response – Interested in Meeting", time: "11 Aug 2025, 10:22 AM", status: "Interested", color: "admincommu123-status-orange" },
    { title: "Follow-up – Assigned to Booth Worker", time: "11 Aug 2025, 10:30 AM", status: "In Progress", color: "admincommu123-status-pink" },
  ];

  // ---------- QUICK ACTIONS ----------
  const quickActions = [
    { icon: <FaWhatsapp />, label: "Omni WhatsApp Campaign", color: "admincommu123-qa-green" },
    { icon: <FaSms />, label: "Send SMS Campaign", color: "admincommu123-qa-blue" },
    { icon: <FaEnvelope />, label: "Send Email Campaign", color: "admincommu123-qa-orange" },
    { icon: <FaBell />, label: "Send Push Notification", color: "admincommu123-qa-purple" },
    { icon: <FaRobot />, label: "AI Call Campaign", color: "admincommu123-qa-teal" },
    { icon: <FaReply />, label: "Create Auto Reply", color: "admincommu123-qa-indigo" },
    { icon: <FaPhoneVolume />, label: "Call Voters (Manual)", color: "admincommu123-qa-pink" },
    { icon: <FaCalendarPlus />, label: "Schedule Meeting", color: "admincommu123-qa-yellow" },
    { icon: <FaChartBar />, label: "View Analytics", color: "admincommu123-qa-red" },
  ];

  return (
    <div className="admincommu123-page">
      {/* ================= TOP STAT CARDS ================= */}
      <div className="admincommu123-stats-grid">
        {statCards.map((s, i) => (
          <div className="admincommu123-stat-card" key={i}>
            <div className={`admincommu123-stat-icon ${s.color}`}>{s.icon}</div>
            <div className="admincommu123-stat-info">
              <span className="admincommu123-stat-label">{s.label}</span>
              <span className="admincommu123-stat-value">{s.value}</span>
              <span className="admincommu123-stat-change">
                ↑ {s.change} {s.note && <span className="admincommu123-stat-note">{s.note}</span>}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* ================= ROW 1: DONUT / DELIVERY / ENGAGEMENT ================= */}
      <div className="admincommu123-row-3">
        {/* Channel Performance Donut */}
        <div className="admincommu123-card">
          <div className="admincommu123-card-header">
            <h3>Channel Performance Overview</h3>
            <button className="admincommu123-filter-btn">
              This Month <FaFilter />
            </button>
          </div>
          <div className="admincommu123-donut-wrap">
            <div className="admincommu123-donut-chart-box">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={channelData}
                    dataKey="value"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {channelData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              <div className="admincommu123-donut-center-text">
                <span className="admincommu123-donut-total">Total Sent</span>
                <span className="admincommu123-donut-value">6,33,050</span>
                <span className="admincommu123-donut-sub">This Month</span>
              </div>
            </div>

            <div className="admincommu123-donut-legend">
              {channelData.map((c, i) => (
                <div className="admincommu123-legend-item" key={i}>
                  <span className="admincommu123-legend-dot" style={{ background: c.color }} />
                  <span className="admincommu123-legend-label">{c.name}</span>
                  <span className="admincommu123-legend-value">
                    {c.percent} ({c.value.toLocaleString()})
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Delivery Summary */}
        <div className="admincommu123-card">
          <div className="admincommu123-card-header">
            <h3>Delivery Summary</h3>
            <button className="admincommu123-filter-btn">
              This Month <FaFilter />
            </button>
          </div>

          <div className="admincommu123-delivery-grid">
            <div className="admincommu123-delivery-box">
              <span className="admincommu123-delivery-dot admincommu123-dot-blue" />
              <div>
                <span className="admincommu123-delivery-label">Sent</span>
                <span className="admincommu123-delivery-value">6,33,050</span>
              </div>
            </div>
            <div className="admincommu123-delivery-box">
              <span className="admincommu123-delivery-dot admincommu123-dot-green" />
              <div>
                <span className="admincommu123-delivery-label">Delivered</span>
                <span className="admincommu123-delivery-value">5,92,480</span>
              </div>
            </div>
            <div className="admincommu123-delivery-box">
              <span className="admincommu123-delivery-dot admincommu123-dot-red" />
              <div>
                <span className="admincommu123-delivery-label">Failed</span>
                <span className="admincommu123-delivery-value">40,570</span>
              </div>
            </div>
            <div className="admincommu123-delivery-box">
              <span className="admincommu123-delivery-dot admincommu123-dot-purple" />
              <div>
                <span className="admincommu123-delivery-label">Replies</span>
                <span className="admincommu123-delivery-value">32,500</span>
              </div>
            </div>
          </div>

          <div className="admincommu123-readreach">
            <span>Read/Reached</span>
            <b>4,22,310</b>
          </div>

          <ResponsiveContainer width="100%" height={130}>
            <AreaChart data={trendData}>
              <defs>
                <linearGradient id="admincommu123Grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="rate"
                stroke="#16a34a"
                strokeWidth={2}
                fill="url(#admincommu123Grad)"
                dot={{ r: 3, fill: "#16a34a" }}
              />
            </AreaChart>
          </ResponsiveContainer>

          <div className="admincommu123-engagement-rate">
            Engagement Rate <b>66.68%</b>
          </div>
        </div>

        {/* Engagement by Channel */}
        <div className="admincommu123-card">
          <div className="admincommu123-card-header">
            <h3>Engagement by Channel</h3>
            <button className="admincommu123-filter-btn">
              This Month <FaFilter />
            </button>
          </div>

          <div className="admincommu123-hbar-list">
            {engagementData.map((e, i) => (
              <div className="admincommu123-hbar-row" key={i}>
                <span className="admincommu123-hbar-label">{e.channel}</span>
                <div className="admincommu123-hbar-track">
                  <div
                    className="admincommu123-hbar-fill"
                    style={{ width: `${e.rate}%`, background: e.color }}
                  />
                </div>
                <span className="admincommu123-hbar-value">{e.rate}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ================= ROW 2: CAMPAIGNS / PERFORMANCE / TIMELINE ================= */}
      <div className="admincommu123-row-3">
        {/* Top Campaigns */}
        <div className="admincommu123-card">
          <div className="admincommu123-card-header">
            <h3>Top Campaigns</h3>
            <a href="#!" className="admincommu123-viewall-link">View All</a>
          </div>

          <div className="admincommu123-campaign-list">
            {topCampaigns.map((c, i) => (
              <div className="admincommu123-campaign-item" key={i}>
                <div className={`admincommu123-campaign-icon ${c.color}`}>{c.icon}</div>
                <div className="admincommu123-campaign-info">
                  <span className="admincommu123-campaign-title">{c.title}</span>
                  <span className="admincommu123-campaign-meta">
                    {c.channel} • {c.date}
                  </span>
                </div>
                <div className="admincommu123-campaign-stats">
                  <span className="admincommu123-campaign-sent">{c.sent}</span>
                  <span className="admincommu123-campaign-rate">{c.rate}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Campaign Performance */}
        <div className="admincommu123-card">
          <div className="admincommu123-card-header">
            <h3>Recent Campaign Performance</h3>
            <button className="admincommu123-filter-btn">
              This Month <FaFilter />
            </button>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={campaignPerformance}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="channel" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Sent" fill="#16a34a" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Delivered" fill="#3b82f6" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Read" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Replies" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Communication Timeline */}
        <div className="admincommu123-card">
          <div className="admincommu123-card-header">
            <h3>Communication Timeline (Example Voter)</h3>
            <a href="#!" className="admincommu123-viewall-link">View Full History</a>
          </div>

          <div className="admincommu123-voter-box">
            <div className="admincommu123-voter-avatar">RK</div>
            <div>
              <span className="admincommu123-voter-name">Voter: Ravi Kumar</span>
              <span className="admincommu123-voter-meta">
                Booth: 127 • Mandal: Madhur • Mobile: XXXXXX4432
              </span>
            </div>
          </div>

          <div className="admincommu123-timeline-list">
            {timeline.map((t, i) => (
              <div className="admincommu123-timeline-item" key={i}>
                <span className="admincommu123-timeline-dot" />
                <div className="admincommu123-timeline-content">
                  <span className="admincommu123-timeline-title">{t.title}</span>
                  <span className="admincommu123-timeline-time">{t.time}</span>
                </div>
                <span className={`admincommu123-timeline-status ${t.color}`}>{t.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ================= QUICK ACTIONS ================= */}
      <div className="admincommu123-card">
        <div className="admincommu123-card-header">
          <h3>Quick Actions</h3>
        </div>
        <div className="admincommu123-quickactions-grid">
          {quickActions.map((q, i) => (
            <button className="admincommu123-qa-btn" key={i}>
              <span className={`admincommu123-qa-icon ${q.color}`}>{q.icon}</span>
              <span>{q.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ================= COMMUNICATION ARCHITECTURE ================= */}
      <div className="admincommu123-card">
        <div className="admincommu123-card-header">
          <h3>Communication Architecture</h3>
        </div>

        <div className="admincommu123-architecture-flow">
          <div className="admincommu123-arch-node">
            <FaUsers className="admincommu123-arch-icon admincommu123-ic-blue" />
            <span>Voters</span>
          </div>
          <FaArrowRight className="admincommu123-arch-arrow" />

          <div className="admincommu123-arch-node">
            <FaFilter className="admincommu123-arch-icon admincommu123-ic-orange" />
            <span>Segmentation</span>
          </div>
          <FaArrowRight className="admincommu123-arch-arrow" />

          <div className="admincommu123-arch-node admincommu123-arch-highlight">
            <MdCampaign className="admincommu123-arch-icon" />
            <span>Communication Center</span>
          </div>
          <FaArrowRight className="admincommu123-arch-arrow" />

          <div className="admincommu123-arch-channels">
            <div className="admincommu123-arch-node admincommu123-arch-small">
              <FaWhatsapp className="admincommu123-arch-icon admincommu123-ic-green" />
              <span>WhatsApp</span>
            </div>
            <div className="admincommu123-arch-node admincommu123-arch-small">
              <FaSms className="admincommu123-arch-icon admincommu123-ic-blue" />
              <span>SMS</span>
            </div>
            <div className="admincommu123-arch-node admincommu123-arch-small">
              <FaEnvelope className="admincommu123-arch-icon admincommu123-ic-orange" />
              <span>Email</span>
            </div>
            <div className="admincommu123-arch-node admincommu123-arch-small">
              <FaBell className="admincommu123-arch-icon admincommu123-ic-purple" />
              <span>Push</span>
            </div>
            <div className="admincommu123-arch-node admincommu123-arch-small">
              <FaRobot className="admincommu123-arch-icon admincommu123-ic-teal" />
              <span>AI Calling</span>
            </div>
            <div className="admincommu123-arch-node admincommu123-arch-small">
              <FaPhoneVolume className="admincommu123-arch-icon admincommu123-ic-pink" />
              <span>Manual Calling</span>
            </div>
          </div>
          <FaArrowRight className="admincommu123-arch-arrow" />

          <div className="admincommu123-arch-node">
            <FaReply className="admincommu123-arch-icon admincommu123-ic-indigo" />
            <span>Responses &amp; Engagement</span>
          </div>
          <FaArrowRight className="admincommu123-arch-arrow" />

          <div className="admincommu123-arch-node">
            <FaChartBar className="admincommu123-arch-icon admincommu123-ic-blue" />
            <span>Analytics &amp; AI Insights</span>
          </div>
          <FaArrowRight className="admincommu123-arch-arrow" />

          <div className="admincommu123-arch-node">
            <FaUserFriends className="admincommu123-arch-icon admincommu123-ic-orange" />
            <span>Follow-up Tasks (Booth / Volunteer Teams)</span>
          </div>
        </div>
      </div>

      {/* ================= FOOTER ================= */}
      <div className="admincommu123-footer">
        <span>© 2025 MLA Inturi Nageswara Rao Official Portal. All Rights Reserved.</span>
        <span>One Platform. Complete Constituency Communication.</span>
      </div>
    </div>
  );
}

export default AdminCommunicationDashboard;